import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';
import type { Comment } from '@tourfold/libraries/tf-api-client-public-v2';
import {
  AttachmentUploadError,
  type AttachmentUploadErrorReason,
} from '../services/comment-attachments.service';
import { CommentsService } from '../services/comments.service';
import { rewriteResolvedAttachmentUrls } from '../services/rewrite-resolved-attachment-urls';
import { RichEditorComponent } from './rich-editor.component';

/**
 * Inline edit form for an existing comment. Used by `<tf-comment-row>` when the row is in edit
 * mode. Mirrors the composer's body textarea + character counter so the editing UX matches
 * authoring. On cancel the original published body is preserved — the user is editing a
 * server-side body, not building a new one.
 *
 * `requireReason` toggles the moderator path: when true, a required `editReason` field appears
 * above the body textarea and the Save button is disabled until both fields are non-empty.
 * The server enforces the same rule (`comments.moderate` edits require a non-blank reason);
 * this client gating only avoids the round-trip for invalid input.
 */
@Component({
  selector: 'tf-comment-edit-form',
  standalone: true,
  imports: [FormsModule, TranslateModule, ButtonModule, InputTextModule, RichEditorComponent],
  templateUrl: './comment-edit-form.component.html',
  styleUrl: './comment-edit-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentEditFormComponent {
  private readonly commentsService = inject(CommentsService);
  private readonly messageService = inject(MessageService, { optional: true });
  private readonly translate = inject(TranslateService);

  /** Mirrors the server constraint on `body_markdown` (see `UpdateComment` Kotlin DTO). */
  static readonly MAX_BODY_LENGTH = 10_000;

  readonly comment = input.required<Comment>();
  /**
   * When true, render the moderator reason field and require it to be non-empty before allowing
   * Save. Set by the parent based on whether the current user is the comment's author.
   */
  readonly requireReason = input<boolean>(false);

  readonly saved = output<Comment>();
  readonly cancelled = output<void>();
  /**
   * Surfaces the current draft body to the host, primarily so the AI assistant page-context
   * can show the assistant what the user is currently typing in an in-progress edit. Mirrors
   * the composer's `draftChange` output. Emitted on every body signal change including the
   * initial seed from the bound comment.
   */
  readonly draftChange = output<string>();

  protected readonly reasonEl = viewChild<ElementRef<HTMLInputElement>>('reasonEl');

  protected readonly reasonId = `tf-comment-edit-reason-${nextEditFormId++}`;

  protected readonly body = signal('');
  protected readonly editReason = signal('');
  protected readonly submitting = signal(false);
  /** Mirror of the editor's `attachmentIdsChange` — same contract as the composer. */
  protected readonly attachmentIds = signal<readonly string[]>([]);
  /**
   * Latest attachment-upload failure surfaced by `<tf-rich-editor>`, keyed by the failed pill's
   * `localId` so the banner stays in lockstep with the pill — see composer counterpart for the
   * full reasoning.
   */
  protected readonly uploadErrorState = signal<
    { reason: AttachmentUploadErrorReason; localId: string } | null
  >(null);
  protected readonly uploadErrorMessageKey = computed<string | null>(() => {
    const reason = this.uploadErrorState()?.reason;
    if (!reason) return null;
    return `COMMENTS.ATTACHMENT_ERROR.${reason.toUpperCase().replaceAll('-', '_')}`;
  });

  protected readonly remaining = computed(
    () => CommentEditFormComponent.MAX_BODY_LENGTH - this.body().length,
  );
  protected readonly isOverLimit = computed(
    () => this.body().length > CommentEditFormComponent.MAX_BODY_LENGTH,
  );
  protected readonly isEmpty = computed(() => this.body().trim().length === 0);
  protected readonly isReasonMissing = computed(
    () => this.requireReason() && this.editReason().trim().length === 0,
  );
  /**
   * Token-form of the original body, used as the dirty-check baseline. We compare against the
   * rewritten body (not `comment().bodyMarkdown`) because the editor sees the rewritten form on
   * seed; comparing against the original resolved-URL body would mark every untouched edit as
   * "dirty" and let the user submit no-op PATCHes.
   */
  protected readonly originalBody = computed<string>(() => {
    const c = this.comment();
    return rewriteResolvedAttachmentUrls(c.bodyMarkdown, c.attachments);
  });

  /**
   * Full attachment list seeded into the rich-editor's pending row. Inline images appear in
   * the pill row alongside the inline `<img>` in the body so the user sees a single
   * authoritative list of files attached to the comment — the body view and the chip-row view
   * are two surfaces over the same set. The editor dedups inline images against the body
   * tokens internally when computing the wire-side `attachment_ids` snapshot, so re-seeding
   * inline images here doesn't double-count them on submit.
   */
  protected readonly allAttachments = computed(() => this.comment().attachments);

  /**
   * Sorted-string snapshot of the comment's original attachment-id set. Used together with the
   * body comparison as the dirty check — adding or removing files with an otherwise-unchanged
   * body is a legitimate edit, so Save needs to enable for it. Sorted + joined produces a
   * stable comparison key that ignores ordering.
   */
  protected readonly originalAttachmentIdsKey = computed<string>(() =>
    [...this.comment().attachments]
      .map((a) => a.filestoreFileId)
      .sort((a, b) => a.localeCompare(b))
      .join('|'),
  );

  protected readonly attachmentIdsKey = computed<string>(() =>
    [...this.attachmentIds()].sort((a, b) => a.localeCompare(b)).join('|'),
  );

  protected readonly canSubmit = computed(
    () =>
      !this.isEmpty()
      && !this.isOverLimit()
      && !this.isReasonMissing()
      && !this.submitting()
      && (
        this.body() !== this.originalBody()
        || this.attachmentIdsKey() !== this.originalAttachmentIdsKey()
      ),
  );

  constructor() {
    // Seed the body from the current comment whenever the bound row changes. This handles the
    // case where the parent reuses the same component instance for a different row (rare in
    // practice, but cheap to support correctly).
    //
    // The server emits `body_markdown` with **resolved presigned URLs** in image references for
    // display. We rewrite those back to the canonical `attachment:<uuid>` token form before
    // seeding Tiptap, so:
    //   - the Tiptap image node's `src` carries the stable token (not a URL that'll expire),
    //   - the wire-side `attachment_ids` extractor on submit finds the same tokens it expects,
    //   - the user can keep / rearrange / delete inline images naturally during the edit.
    effect(() => {
      const c = this.comment();
      this.body.set(rewriteResolvedAttachmentUrls(c.bodyMarkdown, c.attachments));
      this.editReason.set('');
    });

    // Moderator edits start with the reason field focused; author edits delegate to the rich
    // editor's own `[autofocus]` input which maps to Tiptap's `autofocus: 'end'` mode.
    effect(() => {
      if (!this.requireReason()) return;
      const target = this.reasonEl()?.nativeElement;
      if (target) queueMicrotask(() => target.focus());
    });

    // Surface the in-progress edit body to the host (page-context override picks this up).
    effect(() => {
      this.draftChange.emit(this.body());
    });
  }

  /** Handle to the inner editor so the banner-dismiss handler can call `removePending`. */
  private readonly editor = viewChild.required(RichEditorComponent);

  protected onCancel(): void {
    this.uploadErrorState.set(null);
    this.cancelled.emit();
  }

  protected onUploadError(event: { error: AttachmentUploadError; localId: string }): void {
    this.uploadErrorState.set({ reason: event.error.reason, localId: event.localId });
  }

  /**
   * User clicked X on the banner. Clears the banner AND removes the matching failed pill so the
   * two error surfaces stay in lockstep — see composer counterpart.
   */
  protected dismissUploadError(): void {
    const state = this.uploadErrorState();
    this.uploadErrorState.set(null);
    if (state) this.editor().removePending(state.localId);
  }

  /** Editor told us a pill was removed — clear the banner if it was keyed off the same id. */
  protected onPendingRemoved(localId: string): void {
    if (this.uploadErrorState()?.localId === localId) {
      this.uploadErrorState.set(null);
    }
  }

  protected async onSubmit(): Promise<void> {
    if (!this.canSubmit()) return;
    this.submitting.set(true);
    try {
      const body = this.body().trim();
      const updated = await this.commentsService.update({
        id: this.comment().id,
        bodyMarkdown: body,
        editReason: this.requireReason() ? this.editReason().trim() : undefined,
        // Sourced from the editor's `attachmentIdsChange` snapshot so a Backspaced-out image and
        // a removed non-inline pill both fall out of the wire payload naturally. The PATCH
        // "omit = preserve" convention isn't right for edits — editing a body is a deliberate
        // user action, and the editor's snapshot is the canonical set.
        attachmentIds: this.attachmentIds(),
      });
      this.uploadErrorState.set(null);
      this.saved.emit(updated);
    } catch {
      this.messageService?.add({
        severity: 'error',
        summary: this.translate.instant('COMMENTS.EDIT.ERROR_TITLE'),
        detail: this.translate.instant('COMMENTS.EDIT.ERROR_DETAIL'),
        life: 5000,
      });
    } finally {
      this.submitting.set(false);
    }
  }
}

let nextEditFormId = 0;
